import { z } from 'zod';
import { QuestionnaireAnswerType, QuestionnaireStatus } from '../enums';

const QuestionnaireAnswerTypeSchema = z.enum([
  QuestionnaireAnswerType.TEXT,
  QuestionnaireAnswerType.SCALE,
  QuestionnaireAnswerType.NUMBER,
  QuestionnaireAnswerType.YES_NO,
  QuestionnaireAnswerType.SINGLE_SELECT,
  QuestionnaireAnswerType.MULTISELECT,
]);

export const QuestionnaireQuestionOptionDto = z.object({
  optionText: z.string().min(1),
  order: z.number().int().nonnegative(),
  // 6.11 — choosing this option stops the client's access (key questions)
  stopsAccess: z.boolean().optional(),
});
export type ApiQuestionnaireQuestionOptionDto = z.infer<
  typeof QuestionnaireQuestionOptionDto
>;

export const QuestionnaireQuestionDto = z.object({
  questionText: z.string().min(1),
  description: z.string().nullish(),
  answerType: QuestionnaireAnswerTypeSchema,
  isRequired: z.boolean().optional(),
  // 6.11 — a "key" question: a stopping answer holds the client's access
  isKeyQuestion: z.boolean().optional(),
  // YES_NO only: which answer stops (true = "Yes", false = "No")
  stopAnswerBoolean: z.boolean().nullish(),
  order: z.number().int().nonnegative(),
  numberMin: z.number().nullish(),
  numberMax: z.number().nullish(),
  scaleMin: z.number().nullish(),
  scaleMax: z.number().nullish(),
  scaleStep: z.number().positive().nullish(),
  unitLabel: z.string().nullish(),
  options: z.array(QuestionnaireQuestionOptionDto).optional(),
});
export type ApiQuestionnaireQuestionDto = z.infer<
  typeof QuestionnaireQuestionDto
>;

/** Release of a PAR-Q access hold: the note is the record of why (6.11). */
export const ReleaseHealthHoldDto = z
  .object({ note: z.string().trim().max(2000).optional() })
  .strict();
export type ApiReleaseHealthHoldDto = z.infer<typeof ReleaseHealthHoldDto>;

export const CreateQuestionnaireDto = z.object({
  title: z.string().min(1),
  description: z.string().nullish(),
  isOrg: z.boolean().optional(),
  includeWithInvitation: z.boolean().optional(),
  questions: z.array(QuestionnaireQuestionDto).optional(),
  // The create form offers the same status control as the edit form. Without
  // this field zod stripped it and every questionnaire was born a draft, no
  // matter what the author picked. ARCHIVED is not a state to create in.
  status: z
    .enum([QuestionnaireStatus.DRAFT, QuestionnaireStatus.ACTIVE])
    .optional(),
});
export type ApiCreateQuestionnaireDto = z.infer<typeof CreateQuestionnaireDto>;

export const UpdateQuestionnaireDto = z.object({
  title: z.string().min(1).optional(),
  description: z.string().nullish(),
  isOrg: z.boolean().optional(),
  includeWithInvitation: z.boolean().optional(),
  questions: z.array(QuestionnaireQuestionDto).optional(),
  status: z.nativeEnum(QuestionnaireStatus).optional(),
});
export type ApiUpdateQuestionnaireDto = z.infer<typeof UpdateQuestionnaireDto>;

// Validated at the pipe so a mistyped status is a 400, not a Postgres enum
// error surfacing as a 500.
export const ListQuestionnairesQueryDto = z.object({
  status: z.nativeEnum(QuestionnaireStatus).optional(),
});
export type ApiListQuestionnairesQueryDto = z.infer<
  typeof ListQuestionnairesQueryDto
>;

export const QuestionnaireAnswerDto = z.object({
  questionId: z.string().uuid(),
  textValue: z.string().optional(),
  numberValue: z.number().optional(),
  booleanValue: z.boolean().optional(),
  selectedOptionIds: z.array(z.string().uuid()).optional(),
});
export type ApiQuestionnaireAnswerDto = z.infer<typeof QuestionnaireAnswerDto>;

export const SubmitQuestionnaireAnswersDto = z.object({
  answers: z.array(QuestionnaireAnswerDto),
});
export type ApiSubmitQuestionnaireAnswersDto = z.infer<
  typeof SubmitQuestionnaireAnswersDto
>;
